/* eslint-disable */







import { ACTION_TARGETS } from './dictionaries';

function _nullishCoalesce(lhs, rhsFn) { if (lhs != null) { return lhs; } else { return rhsFn(); } } function _optionalChain(ops) { let lastAccessLHS = undefined; let value = ops[0]; let i = 1; while (i < ops.length) { const op = ops[i]; const fn = ops[i + 1]; i += 2; if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) { return undefined; } if (op === 'access' || op === 'optionalAccess') { lastAccessLHS = value; value = fn(value); } else if (op === 'call' || op === 'optionalCall') { value = fn((...args) => value.call(lastAccessLHS, ...args)); lastAccessLHS = undefined; } } return value; }








const HANDLE_THRESHOLD = 55;   // top score must reach this
const AMBIGUITY_GAP = 15;   // gap between top and runner-up; below this = ambiguous

/**
 * Resolve the top intent from scored candidates.
 * Returns confidence 0..1 and isAmbiguous flag.
 */
export function resolveAmbiguity(candidates) {
    if (candidates.length === 0) {
        return { intent: 'unknown', confidence: 0, reasons: ['no intent candidates scored above 0'], isAmbiguous: false };
    }

    const top = candidates[0];
    const runnerUp = candidates[1];

    const gap = runnerUp ? top.score - runnerUp.score : top.score;
    const isAmbiguous = gap < AMBIGUITY_GAP && (_nullishCoalesce(_optionalChain([runnerUp, 'optionalAccess', _ => _.score]), () => (0))) > 0;

    if (top.score < HANDLE_THRESHOLD || isAmbiguous) {
        return {
            intent: isAmbiguous ? 'clarify' : 'unknown',
            confidence: Math.min(top.score / 100, 0.69),
            reasons: [
                ...top.reasons,
                isAmbiguous
                    ? `ambiguous: gap between '${top.intent}' (${top.score}) and '${runnerUp.intent}' (${runnerUp.score}) is only ${gap}`
                    : `low confidence: top score ${top.score} < threshold ${HANDLE_THRESHOLD}`,
            ],
            isAmbiguous,
        };
    }

    // Normalise to 0..1; cap at 1.0
    const confidence = Math.min(top.score / 100, 1.0);
    return { intent: top.intent, confidence, reasons: top.reasons, isAmbiguous: false };
}

/**
 * Build concrete actions for a resolved intent + entity set.
 * Returns an empty array for unknown/clarify (caller will add escalation OPEN_MODAL).
 */
export function buildActions(
    intent,
    entities,
    ctx,
) {
    const actions = [];

    switch (intent) {
        case 'navigate': {
            // Decide list vs templates based on token context (caller passes normalized)
            // We look at ctx for helpful context clues
            if (ctx.current_route.includes('templates')) {
                actions.push({ type: 'NAVIGATE', target: ACTION_TARGETS.POLICY_TEMPLATES });
            } else {
                actions.push({ type: 'NAVIGATE', target: ACTION_TARGETS.POLICY_LIST });
            }
            break;
        }

        case 'policy_open': {
            if (entities.policy_id) {
                actions.push({
                    type: 'NAVIGATE',
                    target: ACTION_TARGETS.POLICY_DETAIL,
                    params: { policy_id: entities.policy_id },
                });
            } else if (entities.policy_title) {
                // Navigate to list with search intent; UI can resolve the title
                actions.push({
                    type: 'NAVIGATE',
                    target: ACTION_TARGETS.POLICY_LIST,
                    params: { search: entities.policy_title, auto_open: true },
                });
            }
            break;
        }

        case 'policy_search': {
            const params = {};
            if (entities.search_query) params.q = entities.search_query;
            if (entities.time_range) params.time_range = entities.time_range;
            actions.push({ type: 'NAVIGATE', target: ACTION_TARGETS.POLICY_LIST, params });
            break;
        }

        case 'template_search': {
            const params = {};
            if (entities.template_query) params.q = entities.template_query;
            actions.push({ type: 'NAVIGATE', target: ACTION_TARGETS.POLICY_TEMPLATES, params });
            break;
        }

        case 'policy_create_from_template': {
            const params = {};
            if (entities.template_query) params.q = entities.template_query;
            if (entities.search_query) params.q = entities.search_query;
            actions.push({ type: 'NAVIGATE', target: ACTION_TARGETS.POLICY_CREATE_FROM_TEMPLATE, params });
            break;
        }

        case 'policy_export_pdf': {
            // Resolve policy_id: explicit entity, or extract from current route
            const policyId = _nullishCoalesce(entities.policy_id, () => (extractPolicyIdFromRoute(ctx.current_route)));
            if (policyId) {
                actions.push({
                    type: 'API_CALL',
                    method: 'POST',
                    path: '/api/policy/exports/pdf',
                    body: { policy_id: policyId },
                });
            } else if (entities.search_query || entities.policy_title) {
                // If no specific ID, navigate with search intent to allow user to find it for export
                const q = entities.search_query || entities.policy_title;
                actions.push({
                    type: 'NAVIGATE',
                    target: ACTION_TARGETS.POLICY_LIST,
                    params: { q },
                });
            }
            break;
        }

        case 'policy_export_batch_pdf': {
            actions.push({
                type: 'API_CALL',
                method: 'POST',
                path: '/api/policy/exports/pdf',
                body: { all_approved: true },
            });
            break;
        }

        case 'policy_show_acknowledgements': {
            const policyId = _nullishCoalesce(entities.policy_id, () => (extractPolicyIdFromRoute(ctx.current_route)));
            if (policyId) {
                actions.push({
                    type: 'NAVIGATE',
                    target: ACTION_TARGETS.POLICY_ACKNOWLEDGEMENTS,
                    params: { policy_id: policyId },
                });
            }
            break;
        }

        case 'policy_show_approvals': {
            const policyId = _nullishCoalesce(entities.policy_id, () => (extractPolicyIdFromRoute(ctx.current_route)));
            if (policyId) {
                actions.push({
                    type: 'NAVIGATE',
                    target: ACTION_TARGETS.POLICY_APPROVALS,
                    params: { policy_id: policyId },
                });
            } else if (entities.policy_title) {
                actions.push({
                    type: 'NAVIGATE',
                    target: ACTION_TARGETS.POLICY_APPROVALS,
                    params: { policy_title: entities.policy_title },
                });
            }
            break;
        }

        case 'policy_show_revision_history': {
            const policyId = _nullishCoalesce(entities.policy_id, () => (extractPolicyIdFromRoute(ctx.current_route)));
            if (policyId) {
                actions.push({
                    type: 'NAVIGATE',
                    target: ACTION_TARGETS.POLICY_REVISION_HISTORY,
                    params: { policy_id: policyId },
                });
            }
            break;
        }

        case 'policy_framework_mapping_view': {
            const params = {};
            if (entities.frameworks.length > 0) params.framework = entities.frameworks[0];
            if (entities.control_ids.length > 0) {
                params.control_ids = entities.control_ids.map(c => c.normalized);
            }
            actions.push({
                type: 'NAVIGATE',
                target: ACTION_TARGETS.POLICY_MAPPINGS,
                params,
            });
            break;
        }

        case 'compliance_readiness_view': {
            const params = {};
            if (entities.frameworks.length > 0) {
                params.framework = entities.frameworks[0];
            }
            actions.push({
                type: 'NAVIGATE',
                target: ACTION_TARGETS.COMPLIANCE_READINESS,
                params,
            });
            break;
        }

        case 'compliance_gaps_view': {
            const params = {};
            if (entities.frameworks.length > 0) {
                params.framework = entities.frameworks[0];
            }
            actions.push({
                type: 'API_CALL',
                method: 'GET',
                path: '/compliance/gaps',
                params,
                target: ACTION_TARGETS.COMPLIANCE_GAPS,
            });
            break;
        }

        case 'compliance_control_view': {
            const params = {};
            // Extract control ID from search query or keywords (e.g. AC.L1-3.1.1)
            if (entities.control_ids && entities.control_ids.length > 0) {
                params.control_id = entities.control_ids[0].normalized;
            } else if (entities.search_query) {
                params.q = entities.search_query;
            }
            if (entities.frameworks.length > 0) {
                params.framework = entities.frameworks[0];
            }
            actions.push({
                type: 'API_CALL',
                method: 'GET',
                path: '/compliance/controls/search',
                params,
                target: ACTION_TARGETS.COMPLIANCE_CONTROL,
            });
            break;
        }

        case 'compliance_report_generate': {
            const params = {};
            if (entities.frameworks.length > 0) {
                params.framework = entities.frameworks[0];
            }
            actions.push({
                type: 'API_CALL',
                method: 'GET',
                path: '/compliance/report',
                params,
                target: ACTION_TARGETS.COMPLIANCE_REPORT,
            });
            break;
        }

        case 'audit_logs_view': {
            actions.push({ type: 'NAVIGATE', target: ACTION_TARGETS.AUDIT_LOGS });
            break;
        }

        case 'help': {
            actions.push({ type: 'OPEN_MODAL', name: 'HelpPanel', props: { tab: 'commands' } });
            break;
        }

        // 'unknown' and 'clarify' → no actions here; escalation.ts appends OPEN_MODAL
        default:
            break;
    }

    return actions;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Extract a policy ID from a route like /policies/P-1023 or /policy/P-1023 */
function extractPolicyIdFromRoute(route) {
    const match = route.match(/\/(?:policy|policies)\/(?:P-|POL-)?(\d+)/i);
    if (match) return `P-${parseInt(match[1], 10)}`;
    return undefined;
}

/** Determine which intents require a policy ID to be actionable */
export const INTENTS_REQUIRING_ID = [
    'policy_export_pdf',
    'policy_show_acknowledgements',
    'policy_show_revision_history',
];

/** Determine which intents can fall back to route-derived ID */
export const INTENTS_WITH_ROUTE_FALLBACK = [
    'policy_export_pdf',
    'policy_show_acknowledgements',
    'policy_show_approvals',
    'policy_show_revision_history',
];
